import { DATE_FORMATS, queryKeys, type DateFormat, type Me, type Permission } from "@l2a/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { ApiError, setUnauthenticatedHandler } from "@/lib/api";
import { configureFormatting } from "@/lib/format";
import { authApi } from "./api";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  /** Set of granted permission keys (empty when signed out). */
  permissions: ReadonlySet<Permission>;
  error: unknown;
  refresh: () => Promise<Me | null>;
  /** Replace the cached session (after login / signup / switch-org). */
  setMe: (me: Me | null) => void;
  signOut: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const EMPTY = new Set<Permission>();

/**
 * Session state from GET /api/v1/auth/me (cached under ["me"]). Any 401 from any
 * request flips the session to signed-out; route guards then redirect to /login.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => authApi.me(signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: (count, err) => !(err instanceof ApiError && err.isClientError) && count < 1,
  });

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      qc.setQueryData(queryKeys.me(), null);
    });
    return () => setUnauthenticatedHandler(null);
  }, [qc]);

  const me = query.data ?? null;

  // Org-level formatting defaults (timezone, date format, base currency).
  if (me) {
    configureFormatting({
      timezone: me.org.timezone || me.user.timezone,
      dateFormat: (DATE_FORMATS as readonly string[]).includes(me.org.dateFormat) ? (me.org.dateFormat as DateFormat) : "DD/MM/YYYY",
      currency: me.org.baseCurrency,
      fyStartMonth: me.org.fyStartMonth,
      locale: me.org.country === "IN" ? "en-IN" : me.user.locale || "en-US",
    });
  }

  const status: AuthStatus = query.isPending ? "loading" : query.isError ? "error" : me ? "authenticated" : "unauthenticated";

  const setMe = useCallback((next: Me | null) => qc.setQueryData(queryKeys.me(), next), [qc]);
  const refresh = useCallback(async () => (await query.refetch()).data ?? null, [query]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* already signed out */
    }
    // Reload onto /login rather than patching the cache. `qc.clear()` removes the
    // ["me"] query WITHOUT notifying its live observer, so the screen went on
    // rendering the old session while the server had already ended it — sign out
    // appeared to do nothing. A full load also drops every cached record, the
    // realtime stream and module state that belonged to the user who just left.
    // replace(): Back must not return to a page from the ended session.
    qc.clear();
    window.location.replace("/login");
  }, [qc]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      const next = await authApi.switchOrg(orgId);
      qc.clear();
      if (next) qc.setQueryData(queryKeys.me(), next);
      else await qc.fetchQuery({ queryKey: queryKeys.me(), queryFn: ({ signal }) => authApi.me(signal) });
    },
    [qc],
  );

  const permissions = useMemo(() => (me ? new Set<Permission>(me.permissions) : EMPTY), [me]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, permissions, error: query.error, refresh, setMe, signOut, switchOrg }),
    [status, me, permissions, query.error, refresh, setMe, signOut, switchOrg],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** The signed-in session. Only call below <RequireAuth> (throws otherwise). */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("useMe() called without a session; wrap the route in <RequireAuth>");
  return me;
}
